import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * Merkt sich pro Gutscheinfeld, ob gerade eine Fehlermeldung angezeigt werden soll.
 * Die Meldung bleibt stehen, bis der Kunde neu tippt, einen neuen Code abschickt,
 * einen Code entfernt oder einen gueltigen Code anwendet. Sie ueberlebt jeden Neuaufbau
 * des Warenkorbs (Drawer, Warenkorb-Seite, Produktseiten), weil sie nach jeder
 * DOM-Aenderung wieder eingeblendet wird.
 * @type {Map<string, 'discount_code' | 'shipping'>}
 */
const persistedErrors = new Map();

let observerStarted = false;

function startErrorObserver() {
  if (observerStarted) return;
  observerStarted = true;

  new MutationObserver(() => {
    if (persistedErrors.size === 0) return;
    for (const element of document.querySelectorAll('cart-discount-component')) {
      if (element instanceof CartDiscount) element.restoreError();
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  connectedCallback() {
    super.connectedCallback();
    startErrorObserver();
    this.addEventListener('input', this.#onInput);
    this.restoreError();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.#onInput);
  }

  /**
   * Sobald der Kunde im Gutscheinfeld tippt, darf die Fehlermeldung verschwinden.
   * @param {Event} event
   */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'discount') {
      this.#clearError();
    }
  };

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    this.#clearError();

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const returnedDiscounts = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const matchingDiscount = returnedDiscounts.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
        return String(discount.code || '').trim().toLowerCase() === discountCodeValue.trim().toLowerCase();
      });

      if (!matchingDiscount || matchingDiscount.applicable === false) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        this.#cancelUpdate(deferredPromise);
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          this.#cancelUpdate(deferredPromise);
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    this.#clearError();

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Beendet das optimistische Update leise, ohne dass andere Bauteile (z. B. die
   * Warenkorb-Positionen) den Warenkorb neu rendern und dabei die Fehlermeldung
   * wieder verstecken. Ein AbortError wird von allen Listenern ohne Fehlerausgabe ignoriert.
   * @param {{ promise: Promise<any>, reject: (reason?: any) => void }} deferredPromise
   */
  #cancelUpdate(deferredPromise) {
    deferredPromise.promise.catch(() => {});
    deferredPromise.reject(new DOMException('Discount code not applicable', 'AbortError'));
  }

  /**
   * Eindeutiger Schluessel dieses Gutscheinfelds (Section + Eingabefeld-ID).
   * @returns {string}
   */
  #errorKey() {
    const input = this.querySelector('input[name="discount"]');
    return `${this.dataset.sectionId ?? ''}:${input?.id ?? ''}`;
  }

  /**
   * Blendet die gemerkte Fehlermeldung wieder ein, falls ein Neuaufbau sie versteckt hat.
   */
  restoreError() {
    const type = persistedErrors.get(this.#errorKey());
    if (type) this.#paintError(type);
  }

  /**
   * @param {'discount_code' | 'shipping'} type
   */
  #paintError(type) {
    const box = this.querySelector('[ref="cartDiscountError"]');
    const codeText = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
    const shippingText = this.querySelector('[ref="cartDiscountErrorShipping"]');
    if (!box || !codeText || !shippingText) return;

    // classList.remove/add schreiben immer das Attribut und wuerden den Beobachter
    // endlos ausloesen, deshalb nur bei echter Aenderung (toggle mit force ist sicher).
    if (box.classList.contains('hidden')) box.classList.remove('hidden');
    codeText.classList.toggle('hidden', type !== 'discount_code');
    shippingText.classList.toggle('hidden', type !== 'shipping');
  }

  #clearError() {
    persistedErrors.delete(this.#errorKey());

    const box = this.querySelector('[ref="cartDiscountError"]');
    const codeText = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
    const shippingText = this.querySelector('[ref="cartDiscountErrorShipping"]');
    box?.classList.toggle('hidden', true);
    codeText?.classList.toggle('hidden', true);
    shippingText?.classList.toggle('hidden', true);
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    // Bewusst kein CartErrorEvent: Er loest andere Warenkorb-Oberflaechen aus, die die
    // Meldung kurz wieder loeschen. Die Meldung bleibt lokal am Gutscheinfeld.
    persistedErrors.set(this.#errorKey(), type);
    this.#paintError(type);
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
